import { memo, type RefObject } from 'react'
import type { ResolvedPart } from '../hooks/use-interleaved-parts'
import { TextPart } from './text-part'
import { ThinkingPart } from './thinking-part'
import { ToolPart } from './tool-part'

// ---------------------------------------------------------------------------
// Part keys — the ONE key contract for message parts.
//
// Keys derive from source identity: the owning message plus the part's index
// *within that message*. Never from counters shared across messages — a
// shared positional counter means a submessage part landing mid-stream shifts
// every later part's key, remounting painted rows (R7e). A within-message
// index can only grow at the tail of its own message, so painted rows never
// reorder.
// ---------------------------------------------------------------------------

/**
 * Key for a text part: owning message + index within that message's parts.
 * `sourceId` should be the message's swap-stable identity (clientId ?? id);
 * callers with an identity-less message pass a positional source of their own
 * (the same documented exception `messageGroupKey` makes).
 */
export function textPartKey(sourceId: string, partIndex: number): string {
  return `text-${sourceId}-${partIndex}`
}

/** Key for a thinking part — one per source message, so no index needed. */
export function thinkingPartKey(sourceId: string): string {
  return `think-${sourceId}`
}

/** Key for a tool-call part — the tool call id is already source identity. */
export function toolPartKey(toolCallId: string): string {
  return `tc-${toolCallId}`
}

// ---------------------------------------------------------------------------
// Part dispatch — part type → leaf renderer.
//
// The leaves own their own subscriptions (thinking-part reads the thinking
// store, tool-part resolves its call from the stable-tool-calls provider), so
// adding a part type is one `PartLeaf` case plus its leaf file.
// ---------------------------------------------------------------------------

export interface PartLeafContext {
  justRevealedRef: RefObject<Set<string>>
  expandedToolIds: Set<string>
  onOpenChange: (toolId: string, isOpen: boolean) => void
  messageId?: string
  conversationId?: string
}

type PartLeafProps = PartLeafContext & { part: ResolvedPart }

/**
 * One rendered part row. The discriminant switch narrows each branch, so a
 * new part type is a new case here plus its leaf file — and forgetting the
 * case fails to compile via the annotated return type.
 */
export const PartLeaf = memo(function PartLeaf({ part, ...ctx }: PartLeafProps): React.ReactElement {
  switch (part.type) {
    case 'text':
      return <TextPart text={part.text} />
    case 'thinking':
      return <ThinkingPart content={part.text} messageId={part.messageId} />
    case 'tool_call':
      return <ToolPart toolCall={part.toolCall} {...ctx} />
  }
})
